import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import CONSTANTS from 'chat-constants';
import GeckoRequest from 'services/gecko-request';
import MessageComponent from './Message';

import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import ConversationEvent from './ConversationEvent';
import {
  getShouldStartNewConversation,
  sendMessage,
  getConversationRichTextTranscript,
  openStaticModalWith,
} from 'routes/conversations/utils';
import { selectAppStateLoggedInUser } from 'reducers/appState_selector';
import { EditorState, convertFromRaw, convertToRaw } from 'draft-js';
import ComposeModal from 'routes/conversations/components/ComposeModal';

const Message = React.forwardRef((props, ref) => {
  const [toPopoverOpen, setToPopoverOpen] = useState(false);
  const messages = useSelector((state) => state.messages);
  const contactsById = useSelector(
    (state) => state.conversationsRoute.contactsById
  );
  const messageSender = useSelector((state) => {
    const senders = [].concat(state.contacts, state.agents);
    if (props.message.sender) {
      return senders.find((s) => s && s.id === props.message.sender.id);
    }
    if (props.message.Sender) {
      return senders.find((s) => s && s.id === props.message.Sender.id);
    }
  });
  const isConversationEvent = props.message.activity;
  const lastMessageChannel = useSelector((state) => {
    const lastMessageChannelId =
      state.conversationsRoute.conversationById[props.message.conversation_id]
        ?.last_message_channel;
    return state.channels.find(
      (channel) => channel.id === lastMessageChannelId
    );
  });
  const agent = useSelector(selectAppStateLoggedInUser);

  const message =
    typeof props.message === 'number'
      ? messages.find((m) => m.id === props.message)
      : props.message;

  if (isConversationEvent) return <ConversationEvent message={message} />;

  const senderChannel = message.sender_channel || message.SenderChannel;
  const isNote = senderChannel?.type === 'internal_note';
  const sender = messageSender || message.sender || message.Sender;

  const isSkillMessage =
    message.skill_type && message.config && message.config.answers;

  const messageIsDisplayListSkill =
    isSkillMessage && message.skill_type === 'DISPLAY_LIST';
  const messageIsSuggestAnswerSkill =
    isSkillMessage && message.skill_type === 'SUGGEST_ANSWER';

  const messageIsBotResponse =
    message.metadata &&
    message.metadata.bot_response_id &&
    message.metadata.bot_id &&
    message.metadata.bot_response_name;

  const messageIsEmail = !!message.metadata?.to?.length;

  const openReplyModal = async (message, channel) => {
    const transcript = await getConversationRichTextTranscript(
      message.conversation_id
    );
    let editorState = EditorState.createEmpty();
    if (message.editor_object) {
      editorState = EditorState.createWithContent(
        convertFromRaw(message.editor_object)
      );
    }
    const messageContent = convertToRaw(editorState.getCurrentContent());

    const replyWithMessage = sendMessage(
      lastMessageChannel,
      channel,
      props.contact.id,
      agent.id,
      message.conversation_id
    );

    const modalProps = {
      message: messageContent,
      transcript: transcript.transcript,
      subject: `RE: ${transcript.subject}`,
      title: 'Reply Via Email',
      sendTo: [props.contact.email],
      availableChannels: [channel],
      channel: channel.id,
      disableToField: true,
      messageType: 'mailgun',
      sendFunction: replyWithMessage,
    };

    openStaticModalWith(<ComposeModal {...modalProps} />);
  };

  const getResendOptions = (message) => {
    if (!message.recipient_channel_id) {
      const msg = messages.find((m) => m.id === message.id);
      if (msg) message.recipient_channel_id = msg.recipient_channel_id;
    }
    const availableChannels = (props.availableChannels || []).filter(
      (channel) => channel.id !== message.recipient_channel_id
    );
    return [{ name: 'Resend', handler: resendMessage }].concat(
      availableChannels.map((channel) => {
        const shouldStartNewConversation = getShouldStartNewConversation(
          channel,
          lastMessageChannel
        );
        const channelMessage = shouldStartNewConversation ? '' : 'Resend on ';
        const handler = shouldStartNewConversation
          ? (message) => openReplyModal(message, channel)
          : (message) => resendMessage(message, channel.id);
        return {
          name: `${channelMessage}${channel.name}`,
          handler,
          id: channel.id,
        };
      })
    );
  };

  const resendMessage = (message, channelId) => {
    const newMessage = Object.assign({}, message, {
      recipient_channel_id: channelId
        ? channelId
        : message.recipient_channel_id,
    });

    GeckoRequest.post('/message/resend', newMessage)
      .then(console.log)
      .catch(console.error);
  };

  const messageHasContent = Boolean(message.content);
  const messageHasEditorObject = Boolean(message.editor_object);
  const messageHasAttachments = message.attachments?.length > 0;

  if (!messageHasContent && !messageHasEditorObject && !messageHasAttachments) {
    return null;
  }

  const isSystemInformation =
    senderChannel?.type === CONSTANTS.CHANNEL.TYPE_SYSTEM_INFORMATION;
  const isDeletedMessage = Boolean(message?.metadata?.deletedAt);
  const isGeckoformResponse =
    senderChannel?.type === CONSTANTS.CHANNEL.TYPE_FORM_RESPONSE;
  const isInstagramMessage =
    senderChannel?.type === CONSTANTS.CHANNEL.TYPE_INSTAGRAM &&
    sender.type !== 'agent';
  const isSystemMessage = isSystemInformation || isGeckoformResponse;
  const isAgent =
    senderChannel?.type === 'gecko_chat' || isNote || sender.type === 'agent';
  const isInboundMessage = message.recipient_id === null;
  const isInboundEmail =
    messageIsEmail === true &&
    isInboundMessage === true &&
    isSystemInformation === false;
  const displayedChannel =
    isAgent && message.recipient_channel
      ? message.recipient_channel
      : senderChannel;

  let senderPresence = sender.presence;
  let senderDetails = contactsById[sender.id];

  // If the Agent is online but unavailble set that status
  if (
    isAgent &&
    senderPresence === 'Online' &&
    !sender.web_available &&
    !sender.mobile_available
  ) {
    senderPresence = 'Unavailable';
  } else if (senderDetails && senderDetails.id === sender.id) {
    senderPresence = senderDetails.presence;
  }

  const messageProps = {
    onAvatarClick: props.onAvatarClick,
    senderPresence,
    sender,
    isSystemMessage,
    isInstagramMessage,
    isDeletedMessage,
    isAgent,
    isInboundEmail,
    isNote,
    style: props.style,
    message,
    displayedChannel,
    messageIsEmail,
    setToPopoverOpen,
    toPopoverOpen,
    getResendOptions,
    messageIsDisplayListSkill,
    messageIsSuggestAnswerSkill,
    messageReady: props.messageReady,
    onMessageClick: props.onMessageClick,
    hideMetadata: props.hideMetadata,
    messageIsBotResponse,
    ref,
  };

  return <MessageComponent {...messageProps} />;
});

Message.defaultProps = {
  onAvatarClick: () => {},
  style: {
    container: {},
    messageContainer: {},
    content: {},
    meta: {},
  },
};

Message.propTypes = {
  message: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({ id: PropTypes.number }),
  ]).isRequired,
  onMessageClick: PropTypes.func,
  onAvatarClick: PropTypes.func,
  style: PropTypes.shape({
    container: PropTypes.object,
    messageContainer: PropTypes.object,
    content: PropTypes.object,
    meta: PropTypes.object,
  }),
  availableChannels: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
    })
  ),
};

export default Message;
